import type { OverSiteControllerPeoplePriceInput } from "@/lib/gql/__generated__/graphql";

import type { ForArrayType } from "@/components/features/_model/types/auto-generator-for-view-model";
import { QuestionSectionWrapper } from "@/components/features/items/edit/_shared/ui/components/system/question/_layouts/item-wrapper";
import { QuestionCardItem } from "@/components/features/items/edit/_shared/ui/components/system/question/card/card-item";
import { QuestionCardItemsWrapper } from "@/components/features/items/edit/_shared/ui/components/system/question/card/card-items-wrapper";

import { getOptionNumArray } from "@/components/ui/form/_utils";
import { InputNumberField } from "@/components/ui/form/fields/input-field/input-number";
import { SelectField } from "@/components/ui/form/fields/select-field";

type Props = {
	prices: ForArrayType<OverSiteControllerPeoplePriceInput[]> | undefined;
	appendItem: () => void;
	canAppendItem: boolean;
	removeItem: (index: number) => void;
	capacityPeopleMaximum: number;
	onSetWeekendPriceInput: (input: number | undefined, index: number) => void;
	onSetWeekendUpperNum: (input: string, index: number) => void;
};

export const PriceInput = ({
	prices,
	appendItem,
	canAppendItem,
	removeItem,
	capacityPeopleMaximum,
	onSetWeekendPriceInput: _onSetWeekendPriceInput,
	onSetWeekendUpperNum: _onSetWeekendUpperNum,
}: Props) => {
	const onSetWeekendPriceInput = (index: number) => (input: number | undefined) => {
		_onSetWeekendPriceInput(input, index);
	};

	const onSetWeekendUpperNum = (index: number) => (input: string) => {
		_onSetWeekendUpperNum(input, index);
	};

	const canRemoveItem = (prices?.value.length ?? 0) > 1;

	return (
		<QuestionCardItemsWrapper appendItem={appendItem} disabled={{ value: !canAppendItem }}>
			{prices?.value.map((item, index) => {
				// 2件目以降の下限人数は、前の項目の上限人数 + 1
				const prevUpperNum = index > 0 ? Number(prices.value[index - 1]?.upperNum.value) : undefined;
				const minUpperNum = prevUpperNum !== undefined && Number.isFinite(prevUpperNum) ? prevUpperNum + 1 : 1;
				const upperNumFieldOptions = getOptionNumArray(
					minUpperNum,
					1,
					Math.max(capacityPeopleMaximum - minUpperNum + 1, 0),
				);

				return (
					<QuestionCardItem
						removeItem={() => removeItem(index)}
						canRemoveItem={canRemoveItem}
						reasonForCannotRemoveItem="固定金額で設定する場合、1つ以上の料金設定が必要です"
						key={index}
					>
						<QuestionSectionWrapper>
							{index === 0 ? <p>設定上限から、宿泊の合計人数</p> : <p>{minUpperNum}人から</p>}
							<SelectField
								placeholder="1"
								value={item?.upperNum.value}
								handleValueChange={onSetWeekendUpperNum(index)}
								options={upperNumFieldOptions}
								errorMessage={undefined}
							/>
							<p>人まで</p>
						</QuestionSectionWrapper>
						<QuestionSectionWrapper>
							<p>お一人様あたり</p>
							<InputNumberField
								value={item?.price?.amount?.value}
								handleInput={onSetWeekendPriceInput(index)}
								errorMessage={item?.price?.amount?.error}
							/>
							<p>円（税込）</p>
						</QuestionSectionWrapper>
					</QuestionCardItem>
				);
			})}
		</QuestionCardItemsWrapper>
	);
};

PriceInput.displayName = "PriceInput";
